'use client';

import { fetchBlogs } from '@eliadmin/core/src/api/blogs/blogs-api';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useInfiniteQuery } from '@tanstack/react-query';
import { BlogCardSkeleton } from './blog-card-skeleton';
import { BlogCard } from './blog-card';
import { FirstBlogCard } from './first-blog-card';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Button } from '../ui/button';

export function BlogsList({ locale }: { locale: string }) {
  const { t } = useTranslations();

  const blogsQuery = useInfiniteQuery({
    queryKey: [CacheKey.BLOGS, locale],
    queryFn: async ({ pageParam = 1 }) => {
      return await fetchBlogs({
        locale,
        query: {
          page: pageParam,
          results: 7,
          order: 'desc',
          orderBy: 'created_at',
        },
      });
    },
    getNextPageParam: (lastPage: any) => {
      if (lastPage?.meta?.current_page < lastPage?.meta?.last_page)
        return lastPage?.meta?.current_page + 1;
      return undefined;
    },
    initialPageParam: 1,
  });

  const blogs = blogsQuery.data?.pages.flatMap((page: any) => page.data) ?? [];
  const isLoading = blogsQuery.isLoading;

  return (
    <section className={cn('custom-section', 'relative z-10 px-0')}>
      <div className={cn('custom-container', 'grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-6')}>
        {isLoading ? (
          [...Array(9)].map((_, index) => <BlogCardSkeleton key={index} index={index} />)
        ) : blogs.length > 0 ? (
          <>
            {blogs.map((blog, index) => {
              if (index === 0) {
                return (
                  <FirstBlogCard
                    blog={blog}
                    key={blog.slug}
                    className={cn(blogs.length > 1 && 'sm:mb-6')}
                  />
                );
              }

              return <BlogCard key={blog.id} blog={blog} />;
            })}
          </>
        ) : (
          <p className="col-span-1 pt-8 text-center text-base font-medium sm:col-span-2 sm:text-lg xl:text-xl">
            {t('text2376')}
          </p>
        )}
      </div>
      <Button
        onClick={() => blogsQuery.fetchNextPage()}
        isLoading={blogsQuery.isFetchingNextPage}
        className={cn('mx-auto mt-8 w-full sm:mt-6 sm:w-fit', {
          hidden: !blogsQuery.hasNextPage,
        })}
        variant={'dark'}
      >
        {t('text3209')}
      </Button>
    </section>
  );
}
